import React, { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { AssetUrls } from 'config/assets';
import CloseIcon from 'components/Icons/CloseIcon';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import { TRADE_MAP_REFERRAL_PARAM } from 'utils/tradeMapShare';
import { LOGIN_TAB_PARAM, LOGIN_TAB_SIGN_IN } from 'screens/Auth/Login/loginTabParam';
import './VisitorStrip.css';

/**
 * The signed-out banner: the first row inside the profile's Trade History
 * card, above the tabs (Asana 1218807104530338, round 3, mocks 19a/19b).
 * Someone arriving from a share link sees what TradeMagic is and how to join
 * in one glance, then the banner stays out of the map's way. Solid fill, no
 * gradient, no transparency: the background art must not show through.
 *
 * Both buttons return the visitor to this profile afterwards: the click stores
 * the current path through the same returnTo slot the import flow uses, and
 * AuthRoute (login) and OnboardingFlow (signup) both navigate to it.
 *
 * The links carry the inbound UTMs and the referral param through to /welcome.
 * AnalyticsTracker already captured them on landing, but capture is last touch
 * and the click may come after other navigation, so the href carries them
 * again. /welcome itself reads only the tab param; the tracker reads the rest.
 */

const DISMISS_KEY = 'profileVisitorStrip.dismissed';
const CARRIED_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', TRADE_MAP_REFERRAL_PARAM];

const readDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    // Private mode can throw on access; the strip just shows
    return false;
  }
};

interface Props {
  username: string;
}

const VisitorStrip: React.FC<Props> = ({ username }) => {
  const [dismissed, setDismissed] = useState(readDismissed);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  if (dismissed) return null;

  const carried = new URLSearchParams();
  CARRIED_PARAMS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) carried.set(key, value);
  });
  const signUpQuery = carried.toString();
  const signUpHref = signUpQuery ? `/welcome?${signUpQuery}` : '/welcome';
  const logInParams = new URLSearchParams(carried);
  logInParams.set(LOGIN_TAB_PARAM, LOGIN_TAB_SIGN_IN);
  const logInHref = `/welcome?${logInParams.toString()}`;

  const rememberReturn = () => {
    ImportSessionStorage.setReturnTo(location.pathname + location.search);
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Storage unavailable: dismissed for this render only
    }
    setDismissed(true);
  };

  return (
    // The wrapper is a size container: the row layout stacks when the banner's
    // own width is narrow, which happens on phones and on desktop while the
    // filter rail is open (the Trade History column is about 267px at 1280).
    <div className="profile-visitor-strip-wrap">
    <div className="profile-visitor-strip" data-testid="profile-visitor-strip" role="region" aria-label="Join TradeMagic">
      <div className="profile-visitor-strip-logo" aria-hidden>
        <img src={AssetUrls.logos.iconWhite} alt="" width={22} height={22} />
      </div>
      <div className="profile-visitor-strip-copy">
        <p className="profile-visitor-strip-headline">
          Login to trade with <span className="profile-visitor-strip-name">{username}</span> today
        </p>
        <p className="profile-visitor-strip-subline">
          Free to join. List your haves and wants, match with players, and ship.
        </p>
      </div>
      <div className="profile-visitor-strip-actions">
        <Link
          to={logInHref}
          className="profile-visitor-strip-btn profile-visitor-strip-btn--ghost"
          onClick={rememberReturn}
          data-testid="profile-visitor-strip-login"
        >
          Log in
        </Link>
        <Link
          to={signUpHref}
          className="profile-visitor-strip-btn profile-visitor-strip-btn--primary"
          onClick={rememberReturn}
          data-testid="profile-visitor-strip-cta"
        >
          Sign up free
        </Link>
      </div>
      <button type="button" className="profile-visitor-strip-dismiss" aria-label="Dismiss" onClick={dismiss}>
        <CloseIcon size={14} />
      </button>
    </div>
    </div>
  );
};

export default VisitorStrip;
